import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { fnJson } from "@/lib/fn-fetch";
import { CloseButton } from "@/components/CloseButton";
import { ExternalLink, Mail, Sparkles, X } from "lucide-react";

// ── Scan inbox for tasks ──────────────────────────────────────────────────
interface Candidate {
  id: string;
  title: string;
  description: string;
  source_from: string | null;
  source_subject: string | null;
  source_email_id: string | null;
  permalink: string;
  dup_reason: string | null;
}
export function ScanInboxModal({ boardId, onClose, onCommitted }: {
  boardId: string;
  onClose: () => void;
  onCommitted: () => Promise<void>;
}) {
  const [phase, setPhase] = useState<"loading" | "not_connected" | "review" | "committing" | "done" | "error">("loading");
  const [connectedEmail, setConnectedEmail] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [scanned, setScanned] = useState(0);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [err, setErr] = useState<string | null>(null);
  const [insertedCount, setInsertedCount] = useState(0);

  useEffect(() => {
    void scan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function scan() {
    setPhase("loading"); setErr(null);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      if (!token) throw new Error("Not signed in.");
      const res = await fetch("/.netlify/functions/scan-inbox-for-tasks", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ board_id: boardId, mode: "preview" }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
      if (body.gmail_not_connected) {
        // Look up whether the caller has a *stale* token (which would mean
        // "connected but revoked"). Not connected at all is the more common
        // case — just show the connect prompt.
        setConnectedEmail(null);
        // HQ-INBOX-001: when the check itself failed rather than simply finding
        // no mailbox, say so and carry the id that matches the server log,
        // instead of a raw database error or a silent "connect" prompt that
        // will not help.
        setErr(body.diagnostic_id
          ? `${body.detail ?? "The mailbox connection could not be checked."} (ref ${body.diagnostic_id})`
          : null);
        setPhase("not_connected");
        return;
      }
      const cands = (body.candidates as Candidate[]) ?? [];
      setCandidates(cands);
      setScanned(body.scanned ?? 0);
      // Pre-check everything except dupes
      setPicked(new Set(cands.filter(c => !c.dup_reason).map(c => c.id)));
      setPhase("review");
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setPhase("error");
    }
  }

  async function commit() {
    setPhase("committing"); setErr(null);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      if (!token) throw new Error("Not signed in.");
      const picks = candidates.filter(c => picked.has(c.id)).map(c => ({
        title: c.title, description: c.description,
      }));
      const res = await fetch("/.netlify/functions/scan-inbox-for-tasks", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ board_id: boardId, mode: "commit", picks }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
      setInsertedCount(body.inserted ?? 0);
      setPhase("done");
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setPhase("error");
    }
  }

  function toggle(id: string) {
    setPicked(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }

  // BE-SEC-006. This used to put the Supabase session token in the query string
  // of a page navigation, because a navigation cannot carry an Authorization
  // header. A token in a URL ends up in browser history, the Referer header and
  // Google's logs. So: fetch the start endpoint WITH the header, and navigate
  // to the URL it returns.
  async function connectGmail() {
    // Relative path only — the function rejects anything else, and an absolute
    // one was an open redirect (BE-SEC-006).
    const back = `${window.location.pathname}?gmail=pending`;
    try {
      const { authorize_url } = await fnJson<{ authorize_url: string }>(
        `/.netlify/functions/gmail-oauth-start?back=${encodeURIComponent(back)}`);
      window.location.href = authorize_url;
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not start the Gmail connection.");
      setPhase("error");
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/60 p-4" onClick={onClose}>
      <div className="card w-full max-w-3xl max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Sparkles size={16} className="text-teal-dark" /> Scan inbox for tasks
          </h2>
          <button onClick={onClose} className="text-gray2 hover:text-bad" aria-label="Close"><X size={18} /></button>
        </div>

        {err && <div className="mb-3 rounded-lg bg-bad-bg px-3 py-2 text-sm text-bad">{err}</div>}

        {phase === "loading" && (
          <div className="py-10 text-center text-sm text-gray2">
            Scanning your inbox for action items…
          </div>
        )}

        {phase === "not_connected" && (
          <div className="py-8 text-center">
            <Mail size={28} className="mx-auto text-teal-dark mb-3" />
            <div className="text-sm font-semibold">Connect your Gmail to scan for tasks</div>
            <div className="mt-1 text-xs text-gray2 max-w-md mx-auto">
              We'll ask Google for read-only access to your inbox so this button can pull recent unread &amp; starred messages and turn actionable ones into tasks. Only you can see the results.
            </div>
            <div className="mt-5 flex justify-center gap-2">
              <button onClick={onClose} className="btn btn-ghost">Cancel</button>
              <button onClick={connectGmail} className="btn">
                <Mail size={14} className="inline mr-1" /> Connect Gmail
              </button>
            </div>
            {connectedEmail && (
              <div className="mt-3 text-[11px] text-gray2">
                Currently connected: {connectedEmail} — reconnect to replace.
              </div>
            )}
          </div>
        )}

        {phase === "done" && (
          <div className="py-10 text-center">
            <div className="text-2xl mb-2">✓</div>
            <div className="text-sm font-semibold">Added {insertedCount} {insertedCount === 1 ? "task" : "tasks"}</div>
            <div className="text-xs text-gray2 mt-1">They landed in the first column, ready to work.</div>
            <div className="mt-4 flex justify-center">
              <button onClick={onClose} className="btn">Done</button>
            </div>
          </div>
        )}

        {phase === "review" && (
          <>
            <div className="mb-3 text-xs text-gray2">
              Scanned {scanned} unread / starred email{scanned === 1 ? "" : "s"} from the last 14 days. AI flagged {candidates.length} possible task{candidates.length === 1 ? "" : "s"}. Uncheck any you don't want, then Add.
            </div>
            {candidates.length === 0 ? (
              <div className="py-10 text-center text-sm text-gray2">
                No new action items found. Inbox zero (or close to it).
              </div>
            ) : (
              <ul className="space-y-2">
                {candidates.map(c => (
                  <li key={c.id} className={`rounded-lg ring-1 p-3 ${c.dup_reason ? "ring-line bg-offwhite/50" : "ring-line"}`}>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={picked.has(c.id)}
                        onChange={() => toggle(c.id)}
                        className="mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-sm">{c.title}</span>
                          {c.dup_reason && <span className="pill bg-warn-bg text-warn text-[10px]">{c.dup_reason}</span>}
                        </div>
                        {c.source_from && (
                          <div className="mt-0.5 text-[11px] text-gray2 truncate">
                            {c.source_from}{c.source_subject ? ` · ${c.source_subject}` : ""}
                          </div>
                        )}
                        {c.description && (
                          <details className="mt-1 text-xs text-gray2">
                            <summary className="cursor-pointer hover:text-teal-dark">Show context</summary>
                            <pre className="mt-1 whitespace-pre-wrap font-sans">{c.description}</pre>
                          </details>
                        )}
                        {c.permalink && (
                          <a href={c.permalink} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-[11px] text-teal-dark hover:underline">
                            open in Gmail <ExternalLink size={9} />
                          </a>
                        )}
                      </div>
                    </label>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-4 flex justify-between items-center">
              <button onClick={scan} className="btn btn-ghost btn-sm">Re-scan</button>
              <div className="flex gap-2">
                <button onClick={onClose} className="btn btn-ghost">Cancel</button>
                <button
                  onClick={commit}
                  className="btn"
                  disabled={picked.size === 0}
                >
                  Add {picked.size || ""} to board
                </button>
              </div>
            </div>
          </>
        )}

        {phase === "committing" && (
          <div className="py-10 text-center text-sm text-gray2">Adding tasks…</div>
        )}

        {phase === "error" && (
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={onClose} className="btn btn-ghost">Close</button>
            <button onClick={scan} className="btn">Retry</button>
          </div>
        )}
      </div>
    </div>
  );
}
